/**
 * PowerConnectionsPanel
 * Reusable panel that shows PDU power connections for any device
 * (server, switch, workstation…) and lets the user add/remove them.
 *
 * Props:
 *   consumerType  – 'server' | 'rack_element' | 'workstation' | 'chassis'
 *   consumerId    – ID of the device
 *   companyId     – active company
 *   rackId        – rack where the device lives (used to pre-filter PDUs)
 *   elementName   – display name (for the heading)
 *   readonly      – disable add/remove buttons
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// ── Cable color helpers ────────────────────────────────────────────────────────

const CABLE_COLOR_HEX = {
  negro:    '#475569',
  rojo:     '#ef4444',
  azul:     '#3b82f6',
  verde:    '#22c55e',
  amarillo: '#eab308',
  blanco:   '#e2e8f0',
  gris:     '#94a3b8',
  naranja:  '#f97316',
  morado:   '#a855f7',
  rosa:     '#ec4899',
};

function cableHex(color) {
  if (!color) return '#6366f1';
  return CABLE_COLOR_HEX[color.toLowerCase()] || '#6366f1';
}

// ── Redundancy badge ──────────────────────────────────────────────────────────

const REDUNDANCY_LABELS = {
  redundant: { label: '✅ Redundante',       color: '#10b981', bg: 'rgba(16,185,129,0.12)'  },
  single:    { label: '⚠️ Fuente única',     color: '#f59e0b', bg: 'rgba(245,158,11,0.12)'  },
  no_power:  { label: '❌ Sin alimentación', color: '#ef4444',  bg: 'rgba(239,68,68,0.12)'   },
};

function redundancyStatus(connections) {
  if (!connections.length) return 'no_power';
  const pduIds = [...new Set(connections.map(c => c.pdu_id))];
  return pduIds.length >= 2 ? 'redundant' : 'single';
}

// ── Mini connection diagram ────────────────────────────────────────────────────

function PowerDiagram({ connections }) {
  if (!connections.length) return (
    <div style={{ color: 'var(--text-muted)', fontSize: '0.83rem', fontStyle: 'italic', padding: '0.75rem 0' }}>
      Sin conexiones registradas.
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', padding: '0.75rem 0' }}>
      {/* Header labels */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: 2 }}>
        <div style={{ minWidth: 100, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', textAlign: 'center' }}>Fuente (PSU)</div>
        <div style={{ flex: 1 }} />
        <div style={{ minWidth: 140, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', textAlign: 'center' }}>Fuente / Enchufe</div>
      </div>
      {connections.map((c, i) => {
        const psuLabel = c.psu_label || (c.psu_component_model ? 'PSU' : `PSU ${i + 1}`);
        const color = cableHex(c.cable_color);
        return (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* PSU node */}
            <div style={{
              background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)',
              borderRadius: 6, padding: '0.3rem 0.6rem', fontSize: '0.78rem',
              color: '#818cf8', fontWeight: 600, minWidth: 100, textAlign: 'center',
            }}>
              ⚡ {psuLabel}
              {c.psu_component_model && (
                <div style={{ fontSize: '0.67rem', color: 'var(--text-muted)', fontWeight: 400, marginTop: 1 }}>{c.psu_component_model}</div>
              )}
            </div>
            {/* Cable line */}
            <div style={{ flex: 1, position: 'relative', height: 28, display: 'flex', alignItems: 'center' }}>
              <div style={{ height: 2, width: '100%', background: `linear-gradient(90deg, rgba(99,102,241,0.5), ${color}, rgba(16,185,129,0.5))`, borderRadius: 1 }} />
              {(c.cable_type || c.cable_color) && (
                <div style={{
                  position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                  background: 'var(--bg-surface)', border: `1px solid ${color}`,
                  borderRadius: 3, padding: '1px 6px',
                  fontSize: '0.67rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                }}>
                  {[c.cable_type, c.cable_color].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
            {/* PDU outlet node */}
            <div style={{
              background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)',
              borderRadius: 6, padding: '0.3rem 0.6rem', fontSize: '0.78rem',
              color: '#10b981', fontWeight: 600, minWidth: 140, textAlign: 'center',
            }}>
              {c.pdu_name}{' '}
              <code style={{ background: 'rgba(16,185,129,0.15)', padding: '1px 4px', borderRadius: 3, fontSize: '0.72rem', color: '#34d399' }}>#{c.outlet_number}</code>
              {c.outlet_type && (
                <div style={{ fontSize: '0.67rem', color: 'var(--text-muted)', fontWeight: 400, marginTop: 1 }}>{c.outlet_type}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PowerConnectionsPanel({
  consumerType,
  consumerId,
  companyId,
  rackId,
  elementName,
  readonly = false,
}) {
  const [connections,  setConnections]  = useState([]);
  const [pdus,         setPdus]         = useState([]);
  const [psuComps,     setPsuComps]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [adding,       setAdding]       = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [viewMode,     setViewMode]     = useState('table'); // 'table' | 'graph'
  const [editingPsu,   setEditingPsu]   = useState(null);   // { connId, psuComponentId, psuLabel }

  const [form, setForm] = useState({
    pdu_id: '', outlet_id: '', psu_component_id: '',
    psu_label: '', cable_type: '', cable_color: '', notes: '',
  });
  const [availableOutlets, setAvailableOutlets] = useState([]);

  // ── load data ───────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!consumerId) return;
    setLoading(true);
    setError(null);
    try {
      const [connRes, pduRes, upsRes] = await Promise.all([
        api.getPowerConnections(consumerType, consumerId, companyId),
        rackId ? api.getPdus(companyId, rackId) : Promise.resolve({ data: [] }),
        api.getUpsList(companyId).catch(() => ({ data: [] })),
      ]);
      setConnections(connRes.data || []);
      const pduSources = (pduRes.data || [])
        .filter(p => p.id !== consumerId)
        .map(p => ({ ...p, _kind: 'pdu' }));
      const upsSources = (upsRes.data || [])
        .filter(u => u.rack_element_id && u.rack_element_id !== consumerId && (u.outlets_total || 0) > 0)
        .map(u => ({
          id: u.rack_element_id,
          _upsId: u.id,
          _kind: 'ups',
          name: u.name,
          rack_id: u.rack_id,
          outlets_total: u.outlets_total,
        }));
      setPdus([...pduSources, ...upsSources]);

      // Load PSU components for this element (server / rack_element / chassis)
      if (consumerId && consumerType !== 'workstation') {
        try {
          const compRes = consumerType === 'chassis'
            ? await api.getChassisComponents(consumerId, companyId)
            : await api.getComponents(consumerId, companyId);
          const psus = (compRes.data || []).filter(c => c.component_type === 'psu');
          setPsuComps(psus);
        } catch {
          setPsuComps([]);
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [consumerType, consumerId, companyId, rackId]);

  useEffect(() => { load(); }, [load]);

  // When the source selection changes, fetch its free outlets
  useEffect(() => {
    if (!form.pdu_id) { setAvailableOutlets([]); return; }
    const source = pdus.find(p => String(p.id) === String(form.pdu_id));
    if (source?._kind === 'ups') {
      api.getUpsOutlets(source._upsId, companyId)
        .then(res => {
          const outlets = (res.data?.outlets || []).filter(
            o => !o.connection && !o.child_pdu_id && o.is_active !== false
          );
          setAvailableOutlets(outlets);
        })
        .catch(() => setAvailableOutlets([]));
      return;
    }
    api.getPdu(form.pdu_id, companyId)
      .then(res => {
        const outlets = (res.data?.outlets || []).filter(o => !o.connection && o.is_active);
        setAvailableOutlets(outlets);
      })
      .catch(() => setAvailableOutlets([]));
  }, [form.pdu_id, companyId, pdus]);

  // ── PSU already connected – compute which psu_component_ids are taken
  const usedPsuComponentIds = new Set(connections.map(c => c.psu_component_id).filter(Boolean));

  // ── handlers ────────────────────────────────────────────────────────────────

  const handleDelete = async (connId) => {
    if (!window.confirm('¿Desconectar este enchufe?')) return;
    try {
      await api.deletePowerConnection(connId, companyId);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleSavePsu = async () => {
    if (!editingPsu) return;
    try {
      await api.updatePowerConnection(editingPsu.connId, {
        psu_component_id: editingPsu.psuComponentId ? parseInt(editingPsu.psuComponentId, 10) : null,
        psu_label: editingPsu.psuLabel || null,
      }, companyId);
      setEditingPsu(null);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleAdd = async () => {
    if (!form.outlet_id) { setError('Elige un enchufe de la fuente'); return; }
    if (!form.psu_component_id && !form.psu_label) {
      setError('Selecciona el componente PSU o escribe una etiqueta'); return;
    }
    setSaving(true);
    setError(null);
    try {
      const source = pdus.find(p => String(p.id) === String(form.pdu_id));
      const payload = {
        psu_component_id: form.psu_component_id ? parseInt(form.psu_component_id, 10) : null,
        psu_label:        form.psu_label || null,
        cable_type:       form.cable_type  || null,
        cable_color:      form.cable_color || null,
        notes:            form.notes       || null,
      };
      if (source?._kind === 'ups') {
        await api.connectUpsOutlet(source._upsId, parseInt(form.outlet_id, 10), {
          ...payload,
          consumer_type: consumerType,
          consumer_id:   consumerId,
        }, companyId);
      } else {
        await api.createPowerConnection({
          ...payload,
          outlet_id:     parseInt(form.outlet_id, 10),
          consumer_type: consumerType,
          consumer_id:   consumerId,
        }, companyId);
      }
      setAdding(false);
      setForm({ pdu_id: '', outlet_id: '', psu_component_id: '', psu_label: '', cable_type: '', cable_color: '', notes: '' });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── render ──────────────────────────────────────────────────────────────────

  const status = redundancyStatus(connections);
  const badge  = REDUNDANCY_LABELS[status];

  if (!consumerId) {
    return (
      <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '1rem 0' }}>
        Guarda el equipo primero para gestionar la alimentación eléctrica.
      </div>
    );
  }

  // ── Inline PSU cell renderer ─────────────────────────────────────────────

  const renderPsuCell = (c, i) => {
    const isEditingThis = editingPsu?.connId === c.id;

    if (isEditingThis) {
      return (
        <td style={{ padding: '0.35rem 0.5rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {psuComps.length > 0 && (
              <select
                value={editingPsu.psuComponentId}
                onChange={e => setEditingPsu(p => ({ ...p, psuComponentId: e.target.value, psuLabel: '' }))}
                style={{ width: '100%', padding: '0.2rem 0.4rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 4, fontSize: '0.78rem', color: 'var(--text-primary)' }}
              >
                <option value="">— Componente PSU —</option>
                {psuComps.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.slot ? `[${p.slot}] ` : ''}{p.manufacturer || ''} {p.model || ''}
                  </option>
                ))}
              </select>
            )}
            <input
              type="text"
              placeholder="Etiqueta (PSU-A…)"
              value={editingPsu.psuLabel}
              onChange={e => setEditingPsu(p => ({ ...p, psuLabel: e.target.value, psuComponentId: '' }))}
              style={{ width: '100%', padding: '0.2rem 0.4rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 4, fontSize: '0.78rem', color: 'var(--text-primary)' }}
            />
            <div style={{ display: 'flex', gap: 4 }}>
              <button type="button" onClick={handleSavePsu}
                style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, padding: '0.15rem 0.5rem', fontSize: '0.73rem', fontWeight: 600, cursor: 'pointer' }}>✓</button>
              <button type="button" onClick={() => setEditingPsu(null)}
                style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 4, padding: '0.15rem 0.5rem', fontSize: '0.73rem', cursor: 'pointer' }}>✕</button>
            </div>
          </div>
        </td>
      );
    }

    const hasPsu = c.psu_component_model || c.psu_label;
    return (
      <td
        style={{ padding: '0.4rem 0.5rem', cursor: readonly ? 'default' : 'pointer' }}
        title={readonly ? undefined : 'Clic para asignar fuente'}
        onClick={() => !readonly && setEditingPsu({ connId: c.id, psuComponentId: c.psu_component_id || '', psuLabel: c.psu_label || '' })}
      >
        {hasPsu ? (
          <>
            <strong style={{ color: 'var(--text-primary)' }}>{c.psu_label || 'PSU'}</strong>
            {c.psu_component_model && (
              <><br /><span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{c.psu_component_model}</span></>
            )}
          </>
        ) : (
          <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.78rem' }}>
            {!readonly ? <span style={{ textDecoration: 'underline dotted' }}>— asignar</span> : '—'}
          </span>
        )}
      </td>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* Status + view toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <span style={{
          background: badge.bg, color: badge.color,
          padding: '0.25rem 0.75rem', borderRadius: 6,
          fontSize: '0.82rem', fontWeight: 600,
          border: `1px solid ${badge.color}33`,
        }}>
          {badge.label}
        </span>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          {connections.length} fuente{connections.length !== 1 ? 's' : ''} conectada{connections.length !== 1 ? 's' : ''}
        </span>

        {connections.length > 0 && (
          <div style={{ marginLeft: 'auto', display: 'flex', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
            {[['table', '☰ Tabla'], ['graph', '⚡ Diagrama']].map(([key, label]) => (
              <button key={key} type="button" onClick={() => setViewMode(key)}
                style={{
                  background: viewMode === key ? 'rgba(99,102,241,0.2)' : 'transparent',
                  color: viewMode === key ? '#818cf8' : 'var(--text-secondary)',
                  border: 'none', padding: '0.25rem 0.75rem',
                  fontSize: '0.78rem', fontWeight: viewMode === key ? 600 : 400,
                  cursor: 'pointer',
                }}>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.3)', padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.82rem' }}>
          ⚠ {error}
        </div>
      )}

      {/* Main view: table or diagram */}
      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Cargando…</div>
      ) : viewMode === 'graph' ? (
        <PowerDiagram connections={connections} />
      ) : connections.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic' }}>
          Sin conexiones de alimentación registradas.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid var(--border)`, textAlign: 'left' }}>
              <th style={{ padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>PSU / Fuente</th>
              <th style={{ padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Fuente</th>
              <th style={{ padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Enchufe</th>
              <th style={{ padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Cable</th>
              <th style={{ padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Notas</th>
              {!readonly && <th style={{ width: 40 }} />}
            </tr>
          </thead>
          <tbody>
            {connections.map((c, i) => (
              <tr key={c.id} style={{ borderBottom: `1px solid var(--border)` }}>
                {renderPsuCell(c, i)}
                <td style={{ padding: '0.4rem 0.5rem', color: 'var(--text-primary)' }}>{c.pdu_name || '—'}</td>
                <td style={{ padding: '0.4rem 0.5rem' }}>
                  <code style={{ background: 'rgba(255,255,255,0.07)', padding: '0.1rem 0.35rem', borderRadius: 3, color: 'var(--text-primary)' }}>
                    {c.outlet_number}
                  </code>
                  {c.outlet_label && c.outlet_label !== `Outlet ${c.outlet_number}` &&
                    <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>{c.outlet_label}</span>}
                  {c.outlet_type && <span style={{ marginLeft: 4, color: 'var(--text-secondary)', fontSize: '0.78rem' }}>({c.outlet_type})</span>}
                </td>
                <td style={{ padding: '0.4rem 0.5rem', color: 'var(--text-secondary)' }}>
                  {c.cable_type || '—'}
                  {c.cable_color && (
                    <span style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: cableHex(c.cable_color), display: 'inline-block' }} />
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{c.cable_color}</span>
                    </span>
                  )}
                </td>
                <td style={{ padding: '0.4rem 0.5rem', color: 'var(--text-muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.notes || '—'}
                </td>
                {!readonly && (
                  <td style={{ padding: '0.25rem 0.25rem', textAlign: 'center' }}>
                    <button type="button" onClick={() => handleDelete(c.id)} title="Desconectar"
                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1rem', padding: 2 }}>
                      🗑
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add button */}
      {!readonly && !adding && (
        <button type="button" onClick={() => setAdding(true)}
          style={{
            alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(16,185,129,0.1)', color: '#10b981',
            border: '1px solid rgba(16,185,129,0.3)',
            borderRadius: 6, padding: '0.35rem 0.75rem', fontSize: '0.82rem',
            fontWeight: 600, cursor: 'pointer',
          }}>
          ⚡ Añadir conexión de alimentación
        </button>
      )}

      {/* Add form */}
      {adding && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem',
        }}>
          <strong style={{ fontSize: '0.88rem', color: 'var(--text-primary)' }}>Nueva conexión de alimentación</strong>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>

            <div>
              <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: 3 }}>Componente PSU</label>
              {psuComps.length > 0 ? (
                <select value={form.psu_component_id} onChange={e => setForm(f => ({ ...f, psu_component_id: e.target.value, psu_label: '' }))}
                  style={{ width: '100%', padding: '0.35rem 0.5rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                  <option value="">— Sin componente —</option>
                  {psuComps.map(p => (
                    <option key={p.id} value={p.id} disabled={usedPsuComponentIds.has(p.id)}>
                      {p.slot ? `[${p.slot}] ` : ''}{p.manufacturer || ''} {p.model || ''}{usedPsuComponentIds.has(p.id) ? ' (ya conectada)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 2 }}>Sin PSUs registradas — usa etiqueta manual</div>
              )}
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: 3 }}>Etiqueta PSU</label>
              <input type="text" placeholder="PSU-A, Fuente 1…" value={form.psu_label}
                onChange={e => setForm(f => ({ ...f, psu_label: e.target.value }))}
                style={{ width: '100%', padding: '0.35rem 0.5rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.82rem', color: 'var(--text-primary)' }} />
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: 3 }}>Fuente (PDU / SAI)</label>
              <select value={form.pdu_id} onChange={e => setForm(f => ({ ...f, pdu_id: e.target.value, outlet_id: '' }))} disabled={!pdus.length}
                style={{ width: '100%', padding: '0.35rem 0.5rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                <option value="">— Selecciona fuente —</option>
                {pdus.map(p => (
                  <option key={`${p._kind}-${p.id}`} value={p.id}>
                    {p._kind === 'ups' ? 'SAI · ' : 'PDU · '}{p.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: 3 }}>Enchufe libre</label>
              <select value={form.outlet_id} onChange={e => setForm(f => ({ ...f, outlet_id: e.target.value }))} disabled={!form.pdu_id}
                style={{ width: '100%', padding: '0.35rem 0.5rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                <option value="">— Selecciona enchufe —</option>
                {availableOutlets.map(o => (
                  <option key={o.id} value={o.id}>
                    #{o.outlet_number}{o.label && o.label !== `Outlet ${o.outlet_number}` ? ` – ${o.label}` : ''}
                    {o.outlet_type ? ` (${o.outlet_type})` : ''}{o.phase ? ` [${o.phase}]` : ''}
                  </option>
                ))}
              </select>
              {form.pdu_id && availableOutlets.length === 0 && (
                <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 3 }}>Sin enchufes disponibles</div>
              )}
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: 3 }}>Tipo de cable</label>
              <select value={form.cable_type} onChange={e => setForm(f => ({ ...f, cable_type: e.target.value }))}
                style={{ width: '100%', padding: '0.35rem 0.5rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                <option value="">— No especificado —</option>
                <option value="IEC C13/C14">IEC C13/C14</option>
                <option value="IEC C19/C20">IEC C19/C20</option>
                <option value="Schuko">Schuko (CEE 7/4)</option>
                <option value="UK BS1363">UK BS1363</option>
                <option value="IEC 309">IEC 309 (industrial)</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: 3 }}>Color del cable</label>
              <input type="text" placeholder="Rojo, Negro…" value={form.cable_color}
                onChange={e => setForm(f => ({ ...f, cable_color: e.target.value }))}
                style={{ width: '100%', padding: '0.35rem 0.5rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.82rem', color: 'var(--text-primary)' }} />
            </div>
          </div>

          <div>
            <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: 3 }}>Notas</label>
            <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              style={{ width: '100%', padding: '0.35rem 0.5rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.82rem', color: 'var(--text-primary)' }} />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={handleAdd} disabled={saving}
              style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '0.4rem 1rem', fontSize: '0.82rem', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Guardando…' : '⚡ Conectar'}
            </button>
            <button type="button" onClick={() => { setAdding(false); setError(null); }}
              style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.4rem 0.875rem', fontSize: '0.82rem', cursor: 'pointer' }}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
